<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <!-- Header -->
    <div class="mb-4">
      <div class="flex items-center gap-3 mb-3">
        <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900">
          {{ pageTitle }}
        </h1>
      </div>
      <div class="flex justify-end">
        <button
          v-if="isEditable && !loading"
          type="button"
          class="inline-flex items-center gap-2 h-10 px-3 rounded-full text-white font-bold text-sm shadow-sm hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
          :style="{ backgroundColor: '#5B9AC5' }"
          :disabled="saving"
          @click="save"
        >
          <svg v-if="saving" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          {{ saving ? 'Saving...' : saveLabel }}
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="animate-pulse space-y-4">
      <div class="h-10 bg-gray-200 rounded w-1/3"></div>
      <div class="bg-white p-6 rounded-2xl shadow space-y-4">
        <div class="h-10 bg-gray-200 rounded"></div>
        <div class="h-10 bg-gray-200 rounded"></div>
        <div class="h-10 bg-gray-200 rounded"></div>
        <div class="grid grid-cols-2 gap-4">
          <div class="h-10 bg-gray-200 rounded"></div>
          <div class="h-10 bg-gray-200 rounded"></div>
          <div class="h-10 bg-gray-200 rounded"></div>
          <div class="h-10 bg-gray-200 rounded"></div>
        </div>
        <div class="h-24 bg-gray-200 rounded"></div>
      </div>
    </div>

    <!-- Form -->
    <template v-else>
      <DynamicForm
        ref="df"
        :schema="computedSchema"
        v-model="form"
        :mode="mode"
        :show-cancel="false"
        :submit-label="'Save'"
        @submit="onSubmit"
      />

      <!-- Manual override toggle for Invoice Amount After Tax -->
      <div
        v-if="isEditable"
        class="mt-3 flex justify-center items-center gap-2"
      >
        <label class="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-600">
          <div
            class="relative inline-flex items-center"
            @click="toggleManual"
          >
            <div
              class="w-10 h-5 rounded-full transition-colors duration-200"
              :class="manualInvoiceAmount ? 'bg-blue-500' : 'bg-gray-300'"
            ></div>
            <div
              class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
              :class="manualInvoiceAmount ? 'translate-x-5' : 'translate-x-0'"
            ></div>
          </div>
          <span>
            Edit <strong>Invoice Amount Before Tax</strong> manually
          </span>
        </label>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/api';
import DynamicForm from '@/components/DynamicForm.vue';
import Swal from 'sweetalert2';

export default {
  name: 'PurchasingFundForm',
  components: { DynamicForm },

  data() {
    return {
      loading: false,
      _loadingData: false,
      saving: false,
      mode: 'add',
      id: null,
      petty_cash_id: null,
      sequence: null,

      manualInvoiceAmount: false,
      manualInvoiceValue: null,

      accountTreeOptions: [],
      vehicleOptions: [],

      form: {
        company_id: '',
        company_name: '',
        account_tree_id: '',
        vehicle_number: '',
        purchasing_id: '',
        contract_id: '',
        date: '',
        supplier_name: '',
        invoice_date: '',
        tax_number: '',
        invoice_number: '',
        total_amount: null,
        tax_percentage: '',
        tax_amount: null,
        invoice_amount_after_tax: null,
        invoice_details: '',
      },
    };
  },

  watch: {
    'form.total_amount'() {
      if (this._loadingData) return;
      this.recalcTax();
    },
    'form.tax_percentage'() {
      if (this._loadingData) return;
      this.recalcTax();
    },
    'form.invoice_amount_after_tax'(val) {
      if (this._loadingData) return;
      if (this.manualInvoiceAmount) {
        this.manualInvoiceValue = val;
      }
    },
  },

  computed: {
    authConfig() {
      const token = localStorage.getItem('token');
      return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
    },

    isView()     { return this.mode === 'view'; },
    isEditable() { return this.mode === 'add' || this.mode === 'edit'; },

    // شركة ليون للسياحة والسفر id=3
    isLyonTourism() {
      return Number(this.form.company_id) === 3;
    },

    pageTitle() {
      if (this.mode === 'add')  return 'Add Purchasing Fund';
      if (this.mode === 'edit') return 'Edit Purchasing Fund';
      return 'View Purchasing Fund';
    },

    saveLabel() { return 'Save'; },

    computedSchema() {
  return {
    sections: [
      {
        cols: 1,
        fields: [
          {
            key: 'company_name',
            label: 'For Company',
            type: 'text',
            placeholder: 'Company Name',
            disabledIf: () => true,
            tabIndex: -1,
          },
        ],
      },
      {
        cols: 1,
        fields: [
          {
            key: 'account_tree_id',
            label: 'Account Tree',
            type: 'select',
            placeholder: 'Select account tree',
            options: this.accountTreeOptions,
            tabIndex: 1,
          },
        ],
      },
      {
        cols: 1,
        fields: [
          {
            key: 'vehicle_number',
            label: 'Vehicle',
            type: 'combobox',
            placeholder: 'Select or type vehicle no.',
            options: this.vehicleOptions,
            tabIndex: 2,
          },
        ],
      },
      ...(this.isLyonTourism ? [{
        cols: 1,
        fields: [
          {
            key: 'contract_id',
            label: 'Contract ID',
            type: 'text',
            placeholder: 'Enter Contract ID',
            tabIndex: 3,
          },
        ],
      }] : []),
      {
        title: 'Purchasing Fund Details',
        cols: 2,
        fields: [
          {
            key: 'purchasing_id',
            label: 'Purchasing ID',
            type: 'text',
            placeholder: this.mode === 'add' ? 'Auto-generated after saving' : 'Purchasing ID',
            disabledIf: () => true,
            tabIndex: -1,
          },
          {
            key: 'date',
            label: 'Date',
            type: 'date',
            disabledIf: () => true,
            tabIndex: -1,
          },
          { key: 'supplier_name',  label: 'Supplier Name',  type: 'text', placeholder: 'Enter Supplier Name', tabIndex: 4 }, // من 1 إلى 4
          { key: 'invoice_date',   label: 'Invoice Date',   type: 'date', tabIndex: 5 }, // من 2 إلى 5
          { key: 'tax_number',     label: 'Tax Number',     type: 'text', placeholder: 'Enter Tax Number', tabIndex: 6 }, // من 3 إلى 6
          { key: 'invoice_number', label: 'Invoice Number', type: 'text', placeholder: 'Enter Invoice Number', tabIndex: 7 }, // من 4 إلى 7
          { key: 'total_amount',   label: 'Total Amount',   type: 'number', placeholder: 'Enter Total Amount', tabIndex: 8 }, // من 5 إلى 8
          {
            key: 'tax_percentage',
            label: 'Tax',
            type: 'select',
            placeholder: 'Select Tax %',
            options: ['0', '5', '10', '16'].map(x => ({ label: `${x}%`, value: x })),
            native: false,
            tabIndex: 9,
          },
          {
            key: 'tax_amount',
            label: 'Tax Amount',
            type: 'number',
            placeholder: 'Tax Amount',
            disabledIf: () => true,
            tabIndex: -1,
            step: '0.001',
            format: (val) => val ? Number(val).toFixed(3) : '0.000',
          },
          {
            key: 'invoice_amount_after_tax',
            label: 'Invoice Amount Before Tax',
            type: 'number',
            placeholder: 'Invoice Amount Before Tax',
            disabledIf: () => !this.manualInvoiceAmount,
            tabIndex: this.manualInvoiceAmount ? 10 : -1,
            step: '0.001',
            format: (val) => val ? Number(val).toFixed(3) : '0.000',
          },
          { key: 'invoice_details', label: 'Invoice Details', type: 'textarea', placeholder: 'Enter Invoice Details...', colSpan: 2, tabIndex: 11 }, // من 8 إلى 11
        ],
      },
    ],
  };
    },
  },

  async mounted() {
    const q  = this.$route?.query   || {};
    const st = window.history.state || {};

    this.mode          = st.pfMode        ?? q.mode          ?? 'add';
    this.id            = st.pfId          ?? q.id            ?? null;
    this.petty_cash_id = st.pfPettyCashId ?? q.petty_cash_id ?? null;
    this.sequence      = st.pfSequence    ?? q.sequence      ?? null;

    const companyId   = st.pfCompanyId   ?? q.company_id   ?? '';
    const companyName = st.pfCompanyName ?? (q.company_name ? decodeURIComponent(q.company_name) : '');

    if (!this.form.date) this.form.date = new Date().toISOString().slice(0, 10);

    if (this.mode === 'add') {
      this.form.purchasing_id = 'Auto-generated after saving';
    } else if (this.sequence) {
      this.form.purchasing_id = this.sequence;
    }

    if (companyId)   this.form.company_id   = isNaN(companyId) ? companyId : Number(companyId);
    if (companyName) this.form.company_name = companyName;

    this.loading = this.mode !== 'add';

    await this.fetchAccountTree();
    if (this.form.company_id) await this.fetchVehicles(this.form.company_id);

    if (this.mode !== 'add') {
      await this.loadData(this.id);
    }

    this.loading = false;
  },

  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: 'PurchasingFund' });
      }
    },

    toggleManual() {
      if (!this.manualInvoiceAmount) {
        this.manualInvoiceValue  = this.form.invoice_amount_after_tax;
        this.manualInvoiceAmount = true;
      } else {
        this.manualInvoiceAmount = false;
        this.form.invoice_amount_after_tax = this.manualInvoiceValue;
      }
    },

    recalcTax() {
  const total  = parseFloat(this.form.total_amount)   || 0;
  const taxPct = parseFloat(this.form.tax_percentage) || 0;

  if (!taxPct) {
    this.form.tax_amount = this.formatToThreeDecimals(total);
    if (!this.manualInvoiceAmount) {
      this.form.invoice_amount_after_tax = this.formatToThreeDecimals(total);
    }
    return;
  }

  // استخدام حسابات دقيقة لتجنب أخطاء التقريب
  const base = total / (1 + taxPct / 100);
  const taxAmt = total - base;

  // تعيين القيم مع 3 أرقام عشرية دون تقريب
  this.form.tax_amount = this.formatToThreeDecimals(taxAmt);
  if (!this.manualInvoiceAmount) {
    this.form.invoice_amount_after_tax = this.formatToThreeDecimals(base);
  }
},

  formatToThreeDecimals(value) {
    const num = Number(value);
    if (isNaN(num)) return '0.000';
    const result = Math.floor(num * 1000) / 1000;
    return result.toFixed(3);
  },

    async fetchAccountTree() {
      try {
        const res  = await api.get('/accounting/purchasing-fund/account-tree-folders', this.authConfig);
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        this.accountTreeOptions = data.map(item => ({
          label: item.name || `#${item.id}`,
          value: item.id,
        }));
      } catch (e) {
        console.error(e);
      }
    },

    async fetchVehicles(companyId) {
      try {
        const res  = await api.get('/accounting/purchasing-fund/vehicles', {
          ...this.authConfig,
          params: { company_id: companyId },
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        this.vehicleOptions = data.map(v => ({
          label: v.vehicle_number || `#${v.id}`,
          value: v.vehicle_number || `#${v.id}`,
        }));
      } catch (e) {
        console.error(e);
      }
    },

    async loadData(id) {
      try {
        const res  = await api.get(`/accounting/purchasing-fund/${id}`, this.authConfig);
        const item = res.data?.data || res.data || {};

        const cleanNumber = (val) => {
          if (val === null || val === undefined || val === '') return null;
          return Number(val);
        };

        const cleanPercent = (val) => {
          if (val === null || val === undefined || val === '') return '';
          return String(parseInt(val));
        };

        const cleaned = {
          ...item,
          total_amount:             cleanNumber(item.total_amount),
          tax_amount:               cleanNumber(item.tax_amount),
          invoice_amount_after_tax: cleanNumber(item.invoice_amount_after_tax),
          tax_percentage:           cleanPercent(item.tax_percentage),
          contract_id:              item.contract_id ?? '',
        };

        this._loadingData = true;
        Object.assign(this.form, cleaned);

        this.form.purchasing_id = cleaned.company_sequence ?? this.sequence ?? this.id;

        await this.$nextTick();
        this._loadingData = false;

        if (item.company_id) this.fetchVehicles(item.company_id);
      } catch (e) {
        this._loadingData = false;
        console.error(e);
      }
    },

    save() {
      this.onSubmit({ ...this.form });
    },

    async onSubmit(payload) {
      this.saving = true;
      try {
        const body = {
          ...payload,
          petty_cash_id:            String(this.petty_cash_id || ''),
          company_id:               String(payload.company_id  || ''),
          tax_amount:               parseFloat(this.form.tax_amount) || 0,
          invoice_amount_after_tax: parseFloat(this.form.invoice_amount_after_tax) || 0,
          ...(this.isLyonTourism
            ? { contract_id: this.form.contract_id || null }
            : {}),
        };

        if (this.mode === 'add') delete body.purchasing_id;

        if (this.mode === 'edit') {
          await api.put(`/accounting/purchasing-fund/${this.id}`, body, this.authConfig);
        } else {
          await api.post('/accounting/purchasing-fund', body, this.authConfig);
        }

        await Swal.fire({
          icon: 'success',
          title: 'Saved successfully',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        });
        this.$router.push({ name: 'PurchasingFund' });
      } catch (e) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to save.',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        });
      }
      finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
:deep(form > .pt-4.flex.items-center.justify-between) { display: none; }

:deep(input[disabled]),
:deep(select[disabled]),
:deep(textarea[disabled]) {
  tabindex: -1;
}
</style>
